import type { ReactElement } from "react";

import { useState, useEffect, useCallback } from "react";

const STORY_PROMPTS: string[] = [
  "A brave little dragon who's afraid of fire...",
  "A magical garden where vegetables come to life...",
  "Two best friends who discover a secret door...",
  "A shy unicorn learning to believe in herself...",
  "A robot who dreams of becoming a chef...",
  "A little owl who wants to see the sunrise...",
  "A magic paintbrush that brings drawings to life...",
  "A tiny mouse with the heart of a lion...",
];

/**
 * TypewriterCarousel component props
 */
interface TypewriterCarouselProps {
  onPromptSelect?: (prompt: string) => void;
  className?: string;
}

/**
 * TypewriterCarousel Component
 *
 * Animated typewriter effect cycling through story prompts.
 */
const TypewriterCarousel = ({
  onPromptSelect,
  className = "",
}: TypewriterCarouselProps): ReactElement => {
  const [currentIndex, setCurrentIndex] = useState<number>(0);
  const [displayedText, setDisplayedText] = useState<string>("");
  const [isTyping, setIsTyping] = useState<boolean>(true);
  const [isDeleting, setIsDeleting] = useState<boolean>(false);

  const currentPrompt = STORY_PROMPTS[currentIndex];

  const typeNextChar = useCallback((): void => {
    if (displayedText.length < currentPrompt.length) {
      setDisplayedText(currentPrompt.slice(0, displayedText.length + 1));
    } else {
      setIsTyping(false);
    }
  }, [displayedText, currentPrompt]);

  const deleteChar = useCallback((): void => {
    if (displayedText.length > 0) {
      setDisplayedText(displayedText.slice(0, -1));
    } else {
      setIsDeleting(false);
      setIsTyping(true);
      setCurrentIndex((prev) => (prev + 1) % STORY_PROMPTS.length);
    }
  }, [displayedText]);

  // Handle typing animation
  useEffect(() => {
    if (isTyping && !isDeleting) {
      const timeout = setTimeout(typeNextChar, 50);
      return () => clearTimeout(timeout);
    }
  }, [isTyping, isDeleting, typeNextChar]);

  // Handle delete animation
  useEffect(() => {
    if (isDeleting) {
      const timeout = setTimeout(deleteChar, 30);
      return () => clearTimeout(timeout);
    }
  }, [isDeleting, deleteChar]);

  // Pause before deleting
  useEffect(() => {
    if (!isTyping && !isDeleting) {
      const timeout = setTimeout(() => {
        setIsDeleting(true);
      }, 3000); // Wait 3 seconds before deleting
      return () => clearTimeout(timeout);
    }
  }, [isTyping, isDeleting]);

  const handleClick = (): void => {
    if (onPromptSelect) {
      onPromptSelect(currentPrompt.replace("...", ""));
    }
  };

  return (
    <div className={`typewriter-carousel ${className}`}>
      <button
        onClick={handleClick}
        className="typewriter-text"
        title="Click to use this prompt"
      >
        <span className="prompt-prefix">Write me a story about </span>
        <span className="prompt-content">{displayedText}</span>
        <span className="cursor">|</span>
      </button>
    </div>
  );
};

export default TypewriterCarousel;
